import React, { useState, useEffect } from 'react';
import { useManagerAuth } from '../../context/ManagerAuthContext';
import { Link } from 'react-router-dom';
import { 
  Package, Search, Warehouse, AlertTriangle, XCircle, CheckCircle2, 
  Filter, Boxes, IndianRupee, Layers, SlidersHorizontal, RefreshCw,
  ArrowRight, ShieldCheck, Tag
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4002';

const ManagerProducts = () => {
  const { getToken, manager } = useManagerAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [stockFilter, setStockFilter] = useState('all'); // all, low, out, instock
  const warehouses = manager?.warehouses || [];

  const fetchProducts = async (s = search, w = filterWarehouse) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (s) params.set('search', s);
    if (w !== 'all') params.set('warehouse_id', w);
    try {
      const res = await fetch(`${API}/api/manager/products?${params}`, { 
        headers: { Authorization: `Bearer ${getToken()}` } 
      });
      const r = await res.json();
      if (r.success) {
        setProducts(r.products || []);
      }
    } catch (e) { 
      console.error('❌ Error fetching manager products:', e); 
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchProducts(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStockStatusBadge = (qty) => {
    if (qty === 0) {
      return (
        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200 flex items-center gap-1 shadow-2xs">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-600 animate-ping" />
          OUT OF STOCK
        </span>
      );
    }
    if (qty <= 10) {
      return (
        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1 shadow-2xs">
          <AlertTriangle className="w-3 h-3 text-amber-600" />
          LOW ({qty})
        </span>
      );
    }
    return (
      <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1 shadow-2xs">
        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
        HEALTHY ({qty})
      </span>
    );
  };

  // Get unique categories list dynamically
  const categories = Array.from(
    new Set(products.map(p => p.category).filter(Boolean))
  ).sort();

  // Filter products by category & stock status
  const filteredProducts = products.filter(p => {
    const matchesCategory = categoryFilter === 'all' || p.category === categoryFilter;
    const totalQty = p.stock?.reduce((acc, s) => acc + (s.quantity || 0), 0) || 0;
    
    let matchesStock = true;
    if (stockFilter === 'low') matchesStock = totalQty > 0 && totalQty <= 10;
    if (stockFilter === 'out') matchesStock = totalQty === 0;
    if (stockFilter === 'instock') matchesStock = totalQty > 10;

    return matchesCategory && matchesStock;
  });

  // Calculate summary metrics
  const totalSKUs = filteredProducts.length;
  const totalUnits = filteredProducts.reduce((acc, p) => {
    const pQty = p.stock?.reduce((sAcc, s) => sAcc + (s.quantity || 0), 0) || 0;
    return acc + pQty;
  }, 0);
  const totalValuation = filteredProducts.reduce((acc, p) => {
    const pQty = p.stock?.reduce((sAcc, s) => sAcc + (s.quantity || 0), 0) || 0;
    return acc + (pQty * (p.price || 0));
  }, 0);

  return (
    <div className="space-y-6 select-none font-sans pb-12">
      
      {/* ── 1. INDIGO VIOLET HERO CONSOLE ── */}
      <div className="relative bg-gradient-to-r from-indigo-950 via-slate-900 to-purple-950 rounded-3xl p-6 sm:p-8 text-white border border-indigo-800/60 shadow-2xl overflow-hidden group">
        
        {/* Soft Radial Backdrops */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-indigo-500/20 via-purple-500/10 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2.5">
              <span className="px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/40 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-xs">
                <Package className="w-3.5 h-3.5 text-indigo-400" />
                PRODUCT CATALOG CONSOLE
              </span>
              <span className="text-xs text-indigo-300 font-mono font-bold">
                ● WAREHOUSE INVENTORY SYNCED
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-black tracking-tight uppercase italic text-white flex items-center gap-3">
              PRODUCT STOCK CATALOG 📦
            </h1>

            <p className="text-xs sm:text-sm text-slate-300 font-medium max-w-xl">
              Inspect all registered product SKUs, prices, categories, and real-time inventory reserves across assigned fulfillment nodes.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={() => fetchProducts(search, filterWarehouse)}
              disabled={loading}
              className="p-3 bg-slate-800/80 hover:bg-slate-700 border border-slate-600 rounded-2xl text-slate-300 hover:text-white transition-all shadow-md active:scale-95 disabled:opacity-50"
              title="Refresh Products"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-indigo-400' : ''}`} />
            </button>

            <Link
              to="/manager/inventory"
              className="px-5 py-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-500 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center gap-2 shadow-lg shadow-indigo-600/30 transition-all hover:scale-105 active:scale-95"
            >
              <Boxes className="w-4 h-4" />
              <span>Stock Adjustments</span>
            </Link>
          </div>
        </div>
      </div>

      {/* ── 2. METRICS TELEMETRY STRIP ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-600">
              <Package className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900 font-mono">{totalSKUs}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Active SKUs</p>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">
            CATALOG
          </span>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
              <Boxes className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900 font-mono">{totalUnits.toLocaleString()}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Units Stored</p>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
            UNITS
          </span>
        </div>

        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-200 flex items-center justify-center text-purple-600">
              <IndianRupee className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xl font-black text-slate-900 font-mono">₹{totalValuation.toLocaleString()}</p>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Estimated Valuation</p>
            </div>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-purple-100 text-purple-800 border border-purple-200">
            VALUE
          </span>
        </div>
      </div>

      {/* ── 3. SEARCH & CONTROLS TOOLBAR ── */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        
        {/* Search Input Box */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchProducts(search, filterWarehouse)}
            placeholder="Search by product name, SKU, or category..."
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-all"
          />
        </div>

        {/* Filters Group */}
        <div className="flex flex-wrap items-center gap-2">
          
          {/* Category Dropdown Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:border-indigo-500 transition-colors"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Warehouse Selector */}
          {warehouses.length > 1 && (
            <select
              value={filterWarehouse}
              onChange={(e) => {
                setFilterWarehouse(e.target.value);
                fetchProducts(search, e.target.value);
              }}
              className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:border-indigo-500 transition-colors"
            >
              <option value="all">All Warehouses</option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>{w.code} – {w.name}</option>
              ))}
            </select>
          )}

          {/* Stock Filter Pills */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200/80">
            {[
              { id: 'all', label: 'All' },
              { id: 'instock', label: 'Healthy' },
              { id: 'low', label: 'Low Stock' },
              { id: 'out', label: 'Out of Stock' },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setStockFilter(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-[11px] font-black uppercase tracking-wider transition-all ${
                  stockFilter === tab.id
                    ? 'bg-white text-indigo-700 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => fetchProducts(search, filterWarehouse)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-transform active:scale-95 shadow-md shadow-indigo-600/20"
          >
            Filter
          </button>
        </div>
      </div>

      {/* ── 4. PRODUCT INVENTORY GRID ── */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-3xl border border-slate-200 p-4 space-y-4 animate-pulse">
              <div className="h-48 bg-slate-100 rounded-2xl" />
              <div className="h-4 bg-slate-100 rounded-md w-3/4" />
              <div className="h-3 bg-slate-100 rounded-md w-1/2" />
            </div>
          ))}
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-16 text-center space-y-4">
          <Package className="w-16 h-16 text-slate-300 mx-auto" />
          <div className="space-y-1">
            <h3 className="text-base font-black uppercase text-slate-800">No Matching Products Found</h3>
            <p className="text-xs font-medium text-slate-500">Try refining your search terms or clearing stock status filters.</p>
          </div>
          <button
            onClick={() => {
              setSearch('');
              setCategoryFilter('all');
              setStockFilter('all');
              setFilterWarehouse('all');
              fetchProducts('', 'all');
            }}
            className="px-5 py-2.5 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 rounded-xl text-xs font-black uppercase tracking-wider transition-colors"
          >
            Reset Filters
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map(p => {
            const totalStock = p.stock?.reduce((acc, s) => acc + (s.quantity || 0), 0) || 0;
            return (
              <div
                key={p.id}
                className="bg-white rounded-3xl overflow-hidden border border-slate-200 hover:border-indigo-400 transition-all duration-300 group flex flex-col hover:-translate-y-1.5 shadow-sm hover:shadow-xl relative"
              >
                {/* Product Image Box */}
                <div className="h-48 bg-gradient-to-b from-slate-50 via-white to-indigo-50/20 p-4 flex items-center justify-center relative overflow-hidden shimmer-effect border-b border-slate-100">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-md"
                    />
                  ) : (
                    <Package className="w-16 h-16 text-slate-300" />
                  )}

                  <span className="absolute top-3 left-3 text-[9px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white text-indigo-700 border border-indigo-200 shadow-xs">
                    {p.category || 'CATALOG'}
                  </span>

                  <div className="absolute top-3 right-3">
                    {getStockStatusBadge(totalStock)}
                  </div>
                </div>

                {/* Card Content Body */}
                <div className="p-5 flex flex-col flex-1 justify-between space-y-4">
                  <div className="space-y-1.5">
                    <h3 className="text-xs font-black text-slate-900 uppercase line-clamp-2 group-hover:text-indigo-600 transition-colors">
                      {p.name}
                    </h3>
                    <div className="flex items-baseline gap-2">
                      <span className="text-lg font-black text-slate-900 font-mono">
                        ₹{Number(p.price || 0).toLocaleString('en-IN')}
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 font-mono">MRP</span>
                    </div>
                  </div>

                  {/* Stock Breakdown Per Assigned Warehouse Node */}
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <p className="text-[9px] font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                      <Warehouse className="w-3 h-3 text-slate-400" />
                      <span>HUB STOCK ALLOCATION</span>
                    </p>

                    {!p.stock || p.stock.length === 0 ? (
                      <div className="flex items-center gap-1.5 text-[11px] font-bold text-rose-500 bg-rose-50 p-2 rounded-xl border border-rose-200">
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                        <span>Unstocked in assigned hubs</span>
                      </div>
                    ) : (
                      p.stock.map(s => (
                        <div
                          key={s.warehouse_id}
                          className="bg-slate-50 border border-slate-200/80 rounded-xl p-2 flex items-center justify-between text-[11px]"
                        >
                          <span className="font-bold text-slate-700 truncate flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                            {s.warehouse?.code || 'HUB'}
                          </span>
                          <span className="font-mono font-black text-slate-900 bg-white px-2 py-0.5 rounded border border-slate-200">
                            {s.quantity} units
                          </span>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Footer Quick Action */}
                  <Link
                    to="/manager/inventory"
                    className="w-full py-2.5 bg-slate-100 hover:bg-indigo-600 text-slate-700 hover:text-white rounded-xl text-xs font-black uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all duration-300 shadow-2xs group/btn mt-auto"
                  >
                    <span>Update Stock</span>
                    <ArrowRight className="w-3.5 h-3.5 group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
};

export default ManagerProducts;

